import type {
  SmartCreationAdGroupDraftContentV2,
  SmartCreationCampaignDraftContentV2,
  SmartCreationStructure,
  SmartCreationStructureEvaluation,
} from "@adpilot/shared";
import { IconAbc, IconCategory, IconPackage, IconTargetArrow } from "@tabler/icons-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export interface StructureTopologyProps {
  evaluation?: SmartCreationStructureEvaluation;
  /** A vendor's products are keyed by ASIN; `sku` repeats it and is not shown (#674). */
  vendor?: boolean;
  loading?: boolean;
}

interface TreeItem {
  key: string;
  content: ReactNode;
  width: number;
}
interface ProductPartition {
  key: string;
  products: Array<{ sku: string; asin: string }>;
  campaigns: SmartCreationCampaignDraftContentV2[];
}

function productPartitions(
  campaigns: readonly SmartCreationCampaignDraftContentV2[],
): ProductPartition[] {
  const partitions = new Map<string, ProductPartition>();
  for (const campaign of campaigns) {
    for (const group of campaign.adGroups) {
      const products = [...group.products].sort((a, b) => a.sku.localeCompare(b.sku));
      const key = JSON.stringify(products.map(({ sku, asin }) => [sku, asin]));
      const partition = partitions.get(key) ?? { key, products, campaigns: [] };
      const existing = partition.campaigns.find((item) => item.itemKey === campaign.itemKey);
      if (existing) existing.adGroups.push(group);
      else partition.campaigns.push({ ...campaign, adGroups: [group] });
      partitions.set(key, partition);
    }
  }
  return [...partitions.values()];
}

interface AdGroupNodeModel {
  key: string;
  groups: SmartCreationAdGroupDraftContentV2[];
}
type DraftTarget = SmartCreationAdGroupDraftContentV2["targeting"]["targets"][number];

function adGroupNodes(campaign: SmartCreationCampaignDraftContentV2, aggregate: boolean) {
  const nodes = new Map<string, AdGroupNodeModel>();
  for (const group of campaign.adGroups) {
    const strategies = [
      ...new Set(group.targeting.targets.map((target) => target.strategyKind)),
    ].sort();
    const lane =
      group.targeting.type === "keyword"
        ? `keyword:${group.targeting.matchType}`
        : group.targeting.type;
    // Binary repeats a strategy/lane for each product group. Coalesce only its drawing;
    // the tooltip and counts retain every distinct executable Ad Group and Target.
    const key = aggregate ? `${campaign.itemKey}:${strategies.join(",")}:${lane}` : group.itemKey;
    const existing = nodes.get(key);
    if (existing) existing.groups.push(group);
    else nodes.set(key, { key, groups: [group] });
  }
  return [...nodes.values()];
}

export function StructureTopology({
  evaluation,
  loading = false,
  vendor = false,
}: StructureTopologyProps) {
  const { t } = useTranslation("smartCreation");
  const [selectedPartitionKey, setSelectedPartitionKey] = useState<string | null>(null);
  const draftContent = evaluation?.draftContent;
  if (!draftContent) {
    return loading && !evaluation ? (
      <Skeleton className="min-h-64 w-full rounded-md" />
    ) : (
      <div
        data-structure-state={evaluation ? "blocked" : "empty"}
        className="flex min-h-64 items-center justify-center text-xs text-muted-foreground"
      >
        {evaluation ? t("structure.blocked") : "—"}
      </div>
    );
  }
  const partitions = productPartitions(draftContent.campaigns);
  const partition = partitions.find((item) => item.key === selectedPartitionKey) ?? partitions[0];
  const standard = evaluation.structure === "standard";
  const campaigns = standard ? (partition?.campaigns ?? []) : draftContent.campaigns;
  const products = standard
    ? (partition?.products ?? [])
    : [
        ...new Map(
          partitions.flatMap((item) => item.products).map((product) => [product.sku, product]),
        ).values(),
      ];
  const productTitle = products
    .map((product) => (vendor ? product.asin : `${product.sku} · ${product.asin}`))
    .join("\n");
  const productGroupLabel = t("structure.topology.productGroup");
  const partitionIndex = partition ? partitions.indexOf(partition) + 1 : 1;
  const rootKey = standard ? (partition?.key ?? "empty") : "all-products";
  return (
    <section
      data-testid={`structure-diagram-${evaluation.structure}`}
      data-structure-topology
      data-total-campaign-count={draftContent.campaigns.length}
      data-total-ad-group-count={draftContent.campaigns.reduce(
        (sum, campaign) => sum + campaign.adGroups.length,
        0,
      )}
      aria-label={t("structure.topology.diagram")}
      className="flex min-h-64 min-w-0 flex-col items-center px-3 py-5"
    >
      <div
        data-structure-product-group={rootKey}
        data-product-group-count={standard ? 1 : partitions.length}
        title={productTitle}
      >
        {standard && partitions.length > 1 ? (
          <Select value={partition?.key} onValueChange={setSelectedPartitionKey}>
            <SelectTrigger size="compact" aria-label={productGroupLabel}>
              <SelectValue>{`${productGroupLabel} ${partitionIndex}`}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {partitions.map((item, index) => (
                <SelectItem
                  key={item.key}
                  value={item.key}
                  title={item.products.map((product) => product.sku).join(", ")}
                >
                  {productGroupLabel} {index + 1}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="rounded-md border border-line bg-subtle px-3 py-1.5 text-center text-[11px] font-medium">
            {productGroupLabel} {standard ? partitionIndex : `· ${partitions.length}`}
          </div>
        )}
      </div>
      <TreeChildren
        parentKey={rootKey}
        items={campaigns.map((campaign) => {
          const nodeCount = adGroupNodes(campaign, evaluation.structure === "binary").length;
          return {
            key: campaign.itemKey,
            width: Math.max(64, nodeCount * 48 + (nodeCount - 1) * 4),
            content: <CampaignNode campaign={campaign} structure={evaluation.structure} />,
          };
        })}
      />
    </section>
  );
}

function CampaignNode({
  campaign,
  structure,
}: {
  campaign: SmartCreationCampaignDraftContentV2;
  structure: SmartCreationStructure;
}) {
  const { t } = useTranslation("smartCreation");
  const automatic = campaign.targetingType === "automatic";
  const first = campaign.adGroups[0]?.targeting;
  const groups = adGroupNodes(campaign, structure === "binary");
  const strategyKinds = [
    ...new Set(
      campaign.adGroups.flatMap((group) =>
        group.targeting.targets.map((target) => target.strategyKind),
      ),
    ),
  ];
  const keywordMatches = new Set(
    campaign.adGroups
      .filter((group) => group.targeting.type === "keyword")
      .map((group) => (group.targeting.type === "keyword" ? group.targeting.matchType : null)),
  );
  const label = automatic
    ? t("preview.automatic")
    : structure === "binary"
      ? t("preview.manual")
      : keywordMatches.size > 1 && strategyKinds.length === 1
        ? t(`strategy.${strategyKinds[0]}`)
        : first?.type === "keyword"
          ? t(`review.match.${first.matchType}`)
          : t(`review.targetType.${first?.type ?? "product"}`);
  return (
    <div
      data-structure-campaign={campaign.itemKey}
      className="flex w-full min-w-0 flex-col items-center"
    >
      <div className="flex w-16 max-w-full flex-col items-center gap-1.5" title={campaign.name}>
        <NodeShape shape={automatic ? "circle" : "diamond"} />
        <span className="w-full truncate text-center text-[10px] font-medium">{label}</span>
      </div>
      <TreeChildren
        parentKey={campaign.itemKey}
        compact
        items={groups.map((node) => ({
          key: node.key,
          width: 48,
          content: <AdGroupNode node={node} />,
        }))}
      />
    </div>
  );
}

function AdGroupNode({ node }: { node: AdGroupNodeModel }) {
  const { t } = useTranslation("smartCreation");
  const targeting = node.groups[0]!.targeting;
  const label =
    targeting.type === "keyword"
      ? t(`review.match.${targeting.matchType}`)
      : t(`review.targetType.${targeting.type}`);
  return (
    <div
      data-structure-ad-group={node.key}
      data-ad-group-count={node.groups.length}
      title={node.groups.map((group) => group.name).join("\n")}
      className="flex w-full min-w-0 flex-col items-center"
    >
      <div className="flex w-12 max-w-full flex-col items-center gap-1">
        <NodeShape shape="square" />
        <span
          title={label}
          className="w-full truncate text-center text-[10px] text-muted-foreground"
        >
          {targeting.type === "keyword" ? targeting.matchType[0]!.toUpperCase() : label}
        </span>
        <span className="h-3 text-[9px] text-muted-foreground">
          {node.groups.length > 1 ? `${node.groups.length} AG` : null}
        </span>
      </div>
      <TreeChildren
        parentKey={node.key}
        compact
        items={[{ key: `${node.key}:targets`, width: 48, content: <TargetSummary node={node} /> }]}
      />
    </div>
  );
}

function TargetSummary({ node }: { node: AdGroupNodeModel }) {
  const { t } = useTranslation("smartCreation");
  const targeting = node.groups[0]!.targeting;
  const targets = node.groups.flatMap<DraftTarget>((group) => group.targeting.targets);
  const Icon =
    targeting.type === "keyword"
      ? IconAbc
      : targeting.type === "product"
        ? IconPackage
        : targeting.type === "category"
          ? IconCategory
          : IconTargetArrow;
  return (
    <figure
      data-structure-targets={node.key}
      data-target-count={targets.length}
      data-target-type={targeting.type}
      className="flex max-w-full items-center gap-0.5 text-muted-foreground"
      aria-label={`${t(`review.targetType.${targeting.type}`)} · ${targets.length}`}
    >
      <div className="flex min-w-0 overflow-hidden">
        {targets.slice(0, 2).map((target) => {
          const label =
            "keyword" in target
              ? target.keyword
              : "asin" in target
                ? target.asin
                : "categoryId" in target
                  ? target.categoryId
                  : target.expressionType;
          return (
            <span
              key={target.itemKey}
              data-structure-target-sample={target.itemKey}
              title={label}
              role="img"
              aria-label={label}
              className="grid size-3 shrink-0 place-items-center"
            >
              <Icon className="size-3" stroke={1.75} />
            </span>
          );
        })}
      </div>
      <span className="shrink-0 font-mono text-[10px] tabular-nums">{targets.length}</span>
    </figure>
  );
}

function TreeChildren({
  parentKey,
  items,
  compact = false,
}: {
  parentKey: string;
  items: TreeItem[];
  compact?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <>
      <span className="block h-4 w-px bg-line" aria-hidden="true" />
      <ul
        className={cn(
          "flex w-full min-w-0 items-start justify-center",
          compact ? "gap-1" : "gap-2",
        )}
        style={{
          maxWidth:
            items.reduce((sum, item) => sum + item.width, 0) +
            Math.max(0, items.length - 1) * (compact ? 4 : 8),
        }}
      >
        {items.map((item, index) => (
          <li
            key={item.key}
            className="relative flex min-w-0 flex-col items-center pt-3"
            style={{ flexBasis: item.width, flexGrow: item.width, flexShrink: 1 }}
          >
            <span
              data-parent-key={parentKey}
              data-child-key={item.key}
              aria-hidden="true"
              className={cn(
                "absolute top-0 h-px bg-line",
                compact ? "-left-0.5 -right-0.5" : "-left-1 -right-1",
                index === 0 && "left-1/2",
                index === items.length - 1 && "right-1/2",
              )}
            />
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-0 h-3 w-px -translate-x-1/2 bg-line"
            />
            {item.content}
          </li>
        ))}
      </ul>
    </>
  );
}

function NodeShape({ shape }: { shape: "circle" | "diamond" | "square" }) {
  return (
    <span
      data-structure-shape={shape}
      aria-hidden="true"
      className={cn(
        "block border",
        shape === "circle"
          ? "size-8 rounded-full border-info/50 bg-info/10"
          : shape === "diamond"
            ? "my-1.5 size-5 rotate-45 border-primary/50 bg-primary/10"
            : "size-5 border-line bg-subtle",
      )}
    />
  );
}

export function StructureTopologyLegend() {
  const { t } = useTranslation("smartCreation");
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
      {(["automatic", "manual", "adGroup", "keyword", "product"] as const).map((kind) => (
        <span key={kind} data-structure-legend={kind} className="flex items-center gap-2">
          <span className="grid size-5 place-items-center">
            {kind === "keyword" ? (
              <IconAbc className="size-4" />
            ) : kind === "product" ? (
              <IconPackage className="size-4" />
            ) : (
              <span
                aria-hidden="true"
                className={cn(
                  "block size-3 border border-current",
                  kind === "automatic" && "rounded-full",
                  kind === "manual" && "rotate-45",
                )}
              />
            )}
          </span>
          {t(`structure.topology.${kind}`)}
        </span>
      ))}
    </div>
  );
}
